import { ANIMAL_SIZE_SCALE, type AnimalDefinition } from './types';

const MEERKAT_SOURCE_WIDTH = 716;
const MEERKAT_SOURCE_HEIGHT = 743;
const MEERKAT_WORLD_WIDTH = 184.68 * ANIMAL_SIZE_SCALE;
const MEERKAT_WORLD_HEIGHT = 191.97 * ANIMAL_SIZE_SCALE;
const RACCOON_SOURCE_WIDTH = 716;
const RACCOON_SOURCE_HEIGHT = 743;
const RACCOON_WORLD_WIDTH = 224 * ANIMAL_SIZE_SCALE;
const RACCOON_WORLD_SCALE = RACCOON_WORLD_WIDTH / RACCOON_SOURCE_WIDTH;
const RACCOON_WORLD_HEIGHT = RACCOON_SOURCE_HEIGHT * RACCOON_WORLD_SCALE;
const OTTER_SOURCE_WIDTH = 716;
const OTTER_SOURCE_HEIGHT = 743;
const OTTER_WORLD_WIDTH = 210 * ANIMAL_SIZE_SCALE;
const OTTER_WORLD_SCALE = OTTER_WORLD_WIDTH / OTTER_SOURCE_WIDTH;
const OTTER_WORLD_HEIGHT = OTTER_SOURCE_HEIGHT * OTTER_WORLD_SCALE;
const TAKENOKO_SOURCE_WIDTH = 716;
const TAKENOKO_SOURCE_HEIGHT = 743;
const TAKENOKO_WORLD_WIDTH = 300 * ANIMAL_SIZE_SCALE;
const TAKENOKO_WORLD_SCALE = TAKENOKO_WORLD_WIDTH / TAKENOKO_SOURCE_WIDTH;
const TAKENOKO_WORLD_HEIGHT = TAKENOKO_SOURCE_HEIGHT * TAKENOKO_WORLD_SCALE;
const KOALA_SOURCE_WIDTH = 716;
const KOALA_SOURCE_HEIGHT = 743;
const KOALA_WORLD_WIDTH = 240 * ANIMAL_SIZE_SCALE;
const KOALA_WORLD_SCALE = KOALA_WORLD_WIDTH / KOALA_SOURCE_WIDTH;
const KOALA_WORLD_HEIGHT = KOALA_SOURCE_HEIGHT * KOALA_WORLD_SCALE;

export const ANIMALS: readonly AnimalDefinition[] = [
  {
    id: 'inverse-meerkat', name: '逆ミーアキャット', shortLabel: 'ミーア', role: 'vertical', requiredRole: 'meerkat',
    width: MEERKAT_WORLD_WIDTH, height: MEERKAT_WORLD_HEIGHT,
    density: 0.0017, friction: 0.72, frictionStatic: 0.92, frictionAir: 0.021, restitution: 0.06,
    baseScore: 150, selectionWeight: 1.1, palette: { body: '#b78655', shade: '#69452e', accent: '#ead0a5' }, markings: 'stripes',
    texture: {
      kind: 'image', path: 'assets/animals/reverse-meerkat-drop-shadow-candidate-20260825.png',
      sourceWidth: MEERKAT_SOURCE_WIDTH,
      sourceHeight: MEERKAT_SOURCE_HEIGHT,
      displayScale: { x: MEERKAT_WORLD_WIDTH / MEERKAT_SOURCE_WIDTH, y: MEERKAT_WORLD_HEIGHT / MEERKAT_SOURCE_HEIGHT },
    },
    hitboxMask: {
      path: 'assets/hitbox-masks/reverse-meerkat-hitbox-mask-20260831.png',
      sourceWidth: MEERKAT_SOURCE_WIDTH,
      sourceHeight: MEERKAT_SOURCE_HEIGHT,
    },
    visualCenter: { x: 0, y: 0 },
    centerOfMass: {
      mode: 'authored', sourceNormalized: { x: 0.205, y: 0.85 },
      note: 'The head is intentionally the heavy point.',
    },
    parts: [
      { kind: 'rectangle', x: -53, y: 72, width: 80, height: 48 },
      { kind: 'rectangle', x: 16, y: -11, width: 64, height: 130, angle: 0.32 },
      { kind: 'rectangle', x: -11, y: 51, width: 27, height: 76, angle: 0.52 },
      { kind: 'rectangle', x: 53, y: 47, width: 19, height: 89, angle: -0.05 },
    ],
  },
  {
    id: 'inverse-raccoon', name: '逆アライグマ', shortLabel: 'アライ', role: 'horizontal',
    width: RACCOON_WORLD_WIDTH, height: RACCOON_WORLD_HEIGHT,
    density: 0.0018, friction: 0.75, frictionStatic: 0.95, frictionAir: 0.021, restitution: 0.05,
    baseScore: 165, selectionWeight: 0.75, palette: { body: '#d88943', shade: '#9c5f31', accent: '#f5eee4' }, markings: 'stripes',
    texture: {
      kind: 'image', path: 'assets/animals/reverse-raccoon-source-20260902.png',
      sourceWidth: RACCOON_SOURCE_WIDTH,
      sourceHeight: RACCOON_SOURCE_HEIGHT,
      displayScale: { x: RACCOON_WORLD_SCALE, y: RACCOON_WORLD_SCALE },
    },
    hitboxMask: {
      path: 'assets/hitbox-masks/reverse-raccoon-hitbox-mask-20260902.png',
      sourceWidth: RACCOON_SOURCE_WIDTH,
      sourceHeight: RACCOON_SOURCE_HEIGHT,
    },
    visualCenter: { x: 0, y: 0 },
    centerOfMass: {
      mode: 'authored', sourceNormalized: { x: 0.68, y: 0.83 },
      note: 'The hind legs are intentionally the heavy point.',
    },
    parts: [
      {
        id: 'body', kind: 'polygon', x: 12.1, y: 22.2,
        vertices: [
          { x: -87.8, y: 63.7 }, { x: -75, y: 22.7 }, { x: -21.5, y: -74.9 },
          { x: -5.8, y: -89.6 }, { x: 15.7, y: -96.8 }, { x: 47.3, y: -87.1 },
          { x: 65.8, y: -47.4 }, { x: 72.1, y: 14.6 }, { x: 45.2, y: 64 },
          { x: -24.9, y: 77.1 }, { x: -80, y: 75.6 },
        ],
      },
      { id: 'sunglasses', kind: 'rectangle', x: -93.7, y: 94, width: 35, height: 16.5 },
      { id: 'chain', kind: 'rectangle', x: 95.6, y: 91.1, width: 31, height: 8.5, angle: -0.38 },
    ],
  },
  {
    id: 'inverse-otter', name: '逆ラッコ', shortLabel: 'ラッコ', role: 'unstable',
    width: OTTER_WORLD_WIDTH, height: OTTER_WORLD_HEIGHT,
    density: 0.002, friction: 0.72, frictionStatic: 0.9, frictionAir: 0.021, restitution: 0.05,
    baseScore: 155, selectionWeight: 0.8, palette: { body: '#686a6c', shade: '#3f4143', accent: '#f4eee5' }, markings: 'plain',
    texture: {
      kind: 'image', path: 'assets/animals/reverse-otter-source-20260831.png',
      sourceWidth: OTTER_SOURCE_WIDTH,
      sourceHeight: OTTER_SOURCE_HEIGHT,
      displayScale: { x: OTTER_WORLD_SCALE, y: OTTER_WORLD_SCALE },
    },
    hitboxMask: {
      path: 'assets/hitbox-masks/reverse-otter-hitbox-mask-20260831.png',
      sourceWidth: OTTER_SOURCE_WIDTH,
      sourceHeight: OTTER_SOURCE_HEIGHT,
    },
    visualCenter: { x: 0, y: 0 },
    centerOfMass: {
      mode: 'authored', sourceNormalized: { x: 0.75, y: 0.36 },
      note: 'The shell is intentionally the heavy point.',
    },
    parts: [
      {
        id: 'head', kind: 'polygon', x: -50, y: 72,
        vertices: [
          { x: -54, y: 20 }, { x: -42, y: -14 }, { x: -27, y: -32 },
          { x: 1, y: -37 }, { x: 40, y: -22 }, { x: 71, y: 21 },
          { x: 59, y: 30 }, { x: 4, y: 29 }, { x: -34, y: 26 },
        ],
      },
      {
        id: 'body', kind: 'polygon', x: 16, y: 18,
        vertices: [
          { x: 20, y: -68 }, { x: 59, y: -39 }, { x: 63, y: 18 },
          { x: 31, y: 57 }, { x: -33, y: 76 }, { x: -65, y: 20 },
          { x: -18, y: 12 }, { x: 4, y: -53 },
        ],
      },
      {
        id: 'tail', kind: 'polygon', x: 55, y: -66,
        vertices: [
          { x: -44, y: -28 }, { x: 2, y: -12 }, { x: 48, y: -25 },
          { x: 17, y: 13 }, { x: 20, y: 34 }, { x: -16, y: 18 },
        ],
      },
    ],
  },
  {
    id: 'inverse-takenoko', name: '逆たけのこ', shortLabel: 'たけのこ', role: 'rounded',
    species: 'inverse-takenoko', variantTier: 'NORMAL', variantId: 'inverse-takenoko-normal',
    width: TAKENOKO_WORLD_WIDTH, height: TAKENOKO_WORLD_HEIGHT,
    density: 0.0023, friction: 0.72, frictionStatic: 0.9, frictionAir: 0.021, restitution: 0.05,
    baseScore: 140, selectionWeight: 0.85, palette: { body: '#9e693f', shade: '#754724', accent: '#f7ede0' }, markings: 'plain',
    texture: {
      kind: 'image', path: 'assets/animals/reverse-takenoko-source-20260831.png',
      sourceWidth: TAKENOKO_SOURCE_WIDTH,
      sourceHeight: TAKENOKO_SOURCE_HEIGHT,
      displayScale: { x: TAKENOKO_WORLD_SCALE, y: TAKENOKO_WORLD_SCALE },
    },
    hitboxMask: {
      path: 'assets/hitbox-masks/reverse-takenoko-hitbox-mask-20260831.png',
      sourceWidth: TAKENOKO_SOURCE_WIDTH,
      sourceHeight: TAKENOKO_SOURCE_HEIGHT,
    },
    visualCenter: { x: 0, y: 0 },
    centerOfMass: { mode: 'auto' },
    parts: [{ id: 'silhouette', kind: 'circle', x: 0, y: 0, radius: 64 }],
  },
  {
    id: 'inverse-koala', name: '逆コアラ', shortLabel: 'コアラ', role: 'vertical',
    species: 'inverse-koala', variantTier: 'NORMAL', variantId: 'inverse-koala-normal',
    width: KOALA_WORLD_WIDTH, height: KOALA_WORLD_HEIGHT,
    density: 0.0022, friction: 0.74, frictionStatic: 0.92, frictionAir: 0.021, restitution: 0.05,
    baseScore: 170, selectionWeight: 0.75, palette: { body: '#d88c3d', shade: '#8d572d', accent: '#83a94d' }, markings: 'plain',
    texture: {
      kind: 'image', path: 'assets/animals/reverse-koala-source-20260831.png',
      sourceWidth: KOALA_SOURCE_WIDTH,
      sourceHeight: KOALA_SOURCE_HEIGHT,
      displayScale: { x: KOALA_WORLD_SCALE, y: KOALA_WORLD_SCALE },
    },
    hitboxMask: {
      path: 'assets/hitbox-masks/reverse-koala-hitbox-mask-20260831.png',
      sourceWidth: KOALA_SOURCE_WIDTH,
      sourceHeight: KOALA_SOURCE_HEIGHT,
    },
    visualCenter: { x: 0, y: 0 },
    centerOfMass: {
      mode: 'authored', sourceNormalized: { x: 0.38, y: 0.54 },
      note: 'The heavy center is the koala body, not the tree.',
    },
    parts: [{ id: 'silhouette', kind: 'circle', x: 0, y: 0, radius: 80 }],
  },
] as const;
